<div class="wrapper-replay-sessions">

  <!-- Header & Breadcrumbs -->
  <div class="section-title">
    <div class="header-content-wrap">
      <div class="header-left">
        <a routerLink="/dashboard/replay" [queryParams]="returnVp ? { vp: returnVp, visitor: visitorId } : { visitor: visitorId }" class="btn btn-back flex-shrink-0" [attr.aria-label]="'Back' | translate">
          <i class="fi-rr-arrow-alt-left"></i>
        </a>
        <div class="header-titles">
          <div class="title d-flex align-items-center gap-2 flex-wrap" [attr.data-parent]="'Session Replay' | translate">
            <span>{{ "Sessions" | translate }}</span>
            <span class="badge-visitor" (click)="copyText(visitorId, 'visitor')" [title]="'Click to copy full Visitor ID' | translate">
              <i class="fi fi-rr-user me-1"></i>
              <span class="font-monospace">{{ shortId(visitorId) }}</span>
              <i class="fi" [ngClass]="copiedId === 'visitor' ? 'fi-rr-check text-success ms-1' : 'fi-rr-copy text-muted ms-1'"></i>
            </span>
          </div>
          <p class="subtitle text-muted mb-0">
            {{ "Review recorded interactions and user journeys for this visitor." | translate }}
          </p>
        </div>
      </div>
      <div class="header-right" *ngIf="!isLoading">
        <span class="header-pill-badge">
          <i class="fi fi-rr-video-camera"></i>
          <strong>{{ totalRecord }}</strong>
          <span>{{ "Total Recorded Sessions" | translate }}</span>
        </span>
      </div>
    </div>
  </div>

  <div class="section-main mt-3">

    <!-- KPI Metric Cards -->
    <div class="row g-3 mb-4 kpi-row" *ngIf="!isLoading && sessions.length > 0">
      <div class="col-6 col-lg-3">
        <div class="kpi-card">
          <div class="kpi-icon bg-blue-subtle text-primary">
            <i class="fi fi-rr-play-alt"></i>
          </div>
          <div class="kpi-content">
            <div class="kpi-label">{{ "Filtered Sessions" | translate }}</div>
            <div class="kpi-value">{{ displayedMetrics.totalSessions }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-lg-3">
        <div class="kpi-card">
          <div class="kpi-icon bg-green-subtle text-success">
            <i class="fi fi-rr-time-fast"></i>
          </div>
          <div class="kpi-content">
            <div class="kpi-label">{{ "Avg. Duration" | translate }}</div>
            <div class="kpi-value">{{ formatDuration(displayedMetrics.avgDurationMs) }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-lg-3">
        <div class="kpi-card">
          <div class="kpi-icon bg-purple-subtle text-purple">
            <i class="fi fi-rr-hourglass-end"></i>
          </div>
          <div class="kpi-content">
            <div class="kpi-label">{{ "Total Active Time" | translate }}</div>
            <div class="kpi-value">{{ formatDuration(displayedMetrics.totalDurationMs) }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-lg-3">
        <div class="kpi-card">
          <div class="kpi-icon bg-amber-subtle text-amber">
            <i class="fi fi-rr-browser"></i>
          </div>
          <div class="kpi-content">
            <div class="kpi-label">{{ "Most Visited Page" | translate }}</div>
            <div class="kpi-value text-truncate" [title]="displayedMetrics.topPage">{{ displayedMetrics.topPage }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Warning if disabled -->
    <div class="alert alert-warning d-flex align-items-center mb-3" *ngIf="!sessionReplayEnabled">
      <i class="fi fi-rr-exclamation me-2"></i>
      <div>
        {{ "Session Replay is currently disabled. You can enable it from" | translate }}
        <a routerLink="/dashboard/visitor-intelligence" class="alert-link fw-semibold ms-1">{{ "Visitor Intelligence" | translate }}</a>.
      </div>
    </div>

    <!-- Dismissible Notice Banner -->
    <div class="modern-notice-banner mb-3" *ngIf="showInfoBanner">
      <div class="d-flex align-items-center gap-2">
        <div class="banner-icon">
          <i class="fi fi-rr-info"></i>
        </div>
        <div class="banner-text">
          <strong>{{ "Recording Coverage" | translate }}:</strong>
          {{ "User interactions are recorded on the Homepage, Checkout, Custom Pages, and Landing pages. Sessions are retained for up to 30 days." | translate }}
        </div>
      </div>
      <button type="button" class="btn-banner-dismiss" (click)="dismissNotice()" title="Dismiss notice">
        <i class="fi fi-rr-cross"></i>
      </button>
    </div>

    <!-- Inline Filter & Search Toolbar -->
    <div class="filter-toolbar mb-3">
      <div class="row g-2 align-items-center">
        <!-- Search -->
        <div class="col-12 col-lg-3">
          <div class="search-input-wrapper">
            <i class="fi fi-rr-search search-icon"></i>
            <input
              type="text"
              class="form-control form-control-sm search-input"
              [(ngModel)]="searchQuery"
              [placeholder]="'Search by URL, Page, or Session ID...' | translate"
            />
            <button *ngIf="searchQuery" class="btn-clear-search" (click)="searchQuery = ''">
              <i class="fi fi-rr-cross-small"></i>
            </button>
          </div>
        </div>

        <!-- Date Segmented Filter -->
        <div class="col-12 col-sm-6 col-lg-3 col-xl-3">
          <div class="segmented-control w-100">
            <button
              type="button"
              class="segmented-btn"
              *ngFor="let f of DATE_FILTERS"
              [class.active]="selectedDateFilter.value === f.value"
              (click)="selectDateFilter(f)"
            >
              {{ f.display | translate }}
            </button>
          </div>
        </div>

        <!-- Page Filter Dropdown -->
        <div class="col-12 col-sm-6 col-lg-2 col-xl-2">
          <div ngbDropdown class="d-inline-block w-100">
            <button class="btn btn-sm btn-outline-secondary dropdown-btn w-100 d-flex align-items-center justify-content-between" ngbDropdownToggle>
              <span class="text-truncate">{{ selectedPageFilter.display | translate }}</span>
            </button>
            <div ngbDropdownMenu class="shadow-sm border-0">
              <button
                ngbDropdownItem
                *ngFor="let p of PAGE_FILTERS"
                [class.active]="p.value === selectedPageFilter.value"
                (click)="selectPageFilter(p)"
              >
                {{ p.display | translate }}
              </button>
            </div>
          </div>
        </div>

        <!-- Duration Filter Pills (Inline, No Wrap) -->
        <div class="col-12 col-lg-4 col-xl-4 text-lg-end">
          <div class="d-flex align-items-center justify-content-start justify-content-lg-end gap-1 flex-nowrap duration-pill-group">
            <span class="small text-muted me-1 d-none d-xl-inline flex-shrink-0">{{ "Duration:" | translate }}</span>
            <button
              *ngFor="let df of DURATION_FILTERS"
              type="button"
              class="btn btn-xs duration-pill flex-shrink-0"
              [class.active]="activeDurationFilter === df.value"
              (click)="setDurationFilter(df.value)"
            >
              {{ df.label | translate }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="isLoading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mt-2 text-muted small">{{ "Fetching recorded sessions..." | translate }}</p>
    </div>

    <!-- Empty State: No Sessions Recorded -->
    <div *ngIf="!isLoading && sessions.length === 0" class="empty-state-card text-center py-5">
      <div class="empty-icon-circle mx-auto mb-3">
        <i class="fi fi-rr-video-camera-alt"></i>
      </div>
      <h6 class="empty-title">{{ "No sessions found for this visitor" | translate }}</h6>
      <p class="empty-desc text-muted mb-3">
        {{ "There are no recorded sessions matching the selected date range and page filter." | translate }}
      </p>
      <button class="btn btn-sm btn-outline-primary" (click)="selectDateFilter(DATE_FILTERS[2]); selectPageFilter(PAGE_FILTERS[0]); setDurationFilter('all')">
        <i class="fi fi-rr-refresh me-1"></i> {{ "Reset Filters" | translate }}
      </button>
    </div>

    <!-- Empty State: Filter yielded no results -->
    <div *ngIf="!isLoading && sessions.length > 0 && filteredSessions.length === 0" class="empty-state-card text-center py-5">
      <div class="empty-icon-circle mx-auto mb-3">
        <i class="fi fi-rr-search-alt"></i>
      </div>
      <h6 class="empty-title">{{ "No matching sessions" | translate }}</h6>
      <p class="empty-desc text-muted mb-3">
        {{ "No sessions match your search keyword or selected duration filter." | translate }}
      </p>
      <button class="btn btn-sm btn-outline-secondary" (click)="searchQuery = ''; setDurationFilter('all')">
        {{ "Clear Search & Filters" | translate }}
      </button>
    </div>

    <!-- Sessions List (Modern High-Density Cards) -->
    <div *ngIf="!isLoading && filteredSessions.length > 0">
      <div class="session-list-wrapper">
        <div
          class="session-row-card mb-2"
          *ngFor="let s of filteredSessions"
          [id]="'session-' + s.session_id"
          (click)="openPlayer(s)"
        >
          <div class="row align-items-center g-3">

            <!-- Col 1: Session Identifier & Time -->
            <div class="col-12 col-md-3">
              <div class="d-flex align-items-center gap-2">
                <div class="session-play-avatar">
                  <i class="fi fi-rr-play"></i>
                </div>
                <div>
                  <div class="d-flex align-items-center gap-1">
                    <span class="session-id-pill font-monospace">#{{ shortId(s.session_id) }}</span>
                    <button
                      type="button"
                      class="btn-copy-icon"
                      (click)="copyText(s.session_id, s.session_id, $event)"
                      [title]="'Copy full Session ID' | translate"
                    >
                      <i class="fi" [ngClass]="copiedId === s.session_id ? 'fi-rr-check text-success' : 'fi-rr-copy'"></i>
                    </button>
                  </div>
                  <div class="session-date-time text-muted mt-1">
                    <i class="fi fi-rr-calendar me-1"></i>
                    <span>{{ s.started_at | date: 'dd MMM yyyy, HH:mm' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Col 2: Page & Clean URL -->
            <div class="col-12 col-md-3">
              <div class="page-meta-wrapper">
                <div class="d-flex align-items-center gap-2 mb-1">
                  <span class="badge bg-primary-subtle text-primary border-0 rounded-pill px-2 py-1 small">
                    <i class="fi fi-rr-document me-1"></i>
                    {{ s.page || 'Page' }}
                  </span>
                </div>
                <div class="url-breadcrumb-link text-truncate" [title]="s.page_url">
                  <i class="fi fi-rr-link text-muted me-1"></i>
                  <span class="url-text">{{ cleanUrl(s.page_url) }}</span>
                </div>
              </div>
            </div>

            <!-- Col 3: Duration & Activity Intensity -->
            <div class="col-6 col-md-3">
              <div class="activity-meta">
                <div class="d-flex align-items-center gap-2 mb-1 flex-wrap">
                  <i class="fi fi-rr-stopwatch text-muted"></i>
                  <span class="duration-highlight">{{ formatDuration(getSessionDuration(s)) }}</span>
                </div>
                <div class="activity-meter d-flex align-items-center gap-1" [title]="'Activity Intensity' | translate">
                  <span
                    class="meter-dot"
                    *ngFor="let dot of getActivityDots(getSessionDuration(s))"
                    [class.active]="dot === 1"
                  ></span>
                  <span class="meter-caption ms-1 text-muted">
                    {{ getSessionDuration(s) > 30000 ? ('High' | translate) : (getSessionDuration(s) > 5000 ? ('Normal' | translate) : (getSessionDuration(s) > 1000 ? ('Short' | translate) : ('Initial' | translate))) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Col 4: Action Buttons -->
            <div class="col-6 col-md-3 d-flex justify-content-end align-items-center flex-wrap gap-2 session-actions">
              <!-- AI Recap (the player's "AI Summary & Insights"): opens the player, which starts the summary once the recording loads.
                   The wrapper stops clicks on the disabled button from opening the row. -->
              <span
                class="ai-btn-wrapper"
                [class.is-disabled]="isAiSummaryDisabled(s)"
                [title]="aiSummaryTooltip(s) | translate"
                (click)="$event.stopPropagation()"
              >
                <button
                  type="button"
                  class="btn btn-sm btn-action-ai"
                  data-ai-purpose="session replay summary"
                  [disabled]="isAiSummaryDisabled(s)"
                  [attr.aria-label]="'AI Recap' | translate"
                  (click)="openAiSummary(s, $event)"
                >
                  <i class="fi fi-rr-sparkles"></i>
                  <span>{{ "AI Recap" | translate }}</span>
                </button>
              </span>

              <button
                class="btn btn-sm btn-watch-replay"
                (click)="openPlayer(s); $event.stopPropagation()"
                [title]="'Watch session replay' | translate"
              >
                <i class="fi fi-rr-play me-1"></i>
                <span>{{ "Watch" | translate }}</span>
              </button>
            </div>

          </div>
        </div>
      </div>

      <!-- Pagination Row -->
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 px-2 py-3 pagination-row" *ngIf="totalRecord > pageSize">
        <div>
          <ngb-pagination
            [collectionSize]="totalRecord"
            [(page)]="currentPage"
            [pageSize]="pageSize"
            aria-label="Session pagination"
            [ellipses]="false"
            [rotate]="true"
            [maxSize]="5"
            (pageChange)="onPageChange()"
          ></ngb-pagination>
        </div>
        <div class="pagination-info small text-muted">
          {{ "Showing" | translate }} <b>{{ start + 1 }}–{{ end }}</b> {{ "of" | translate }} <b>{{ totalRecord }}</b>
        </div>
      </div>

    </div>

  </div>

</div>
